import { Component } from "@theme/component";
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from "@theme/utilities";
import { morphSection, sectionRenderer } from "@theme/section-renderer";
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from "@theme/events";
import { cartPerformance } from "@theme/performance";
import { loyaltyThresholdLossMessage, snapshotLoyaltyCodesFromScope } from "./loyalty-discount-utils.js";
/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  // Store bound function references for event listeners
  _onBundleQuantityChange = this.#onBundleQuantityChange.bind(this);
  _onBundleRemove = this.#onBundleRemove.bind(this);
  _handleBundleRemoveClick = this.#handleBundleRemoveClick.bind(this);

  /** @type {boolean} */
  #skipLoyaltyThresholdOnce = false;

  #onDiscountManualRemove = () => {
    this.#skipLoyaltyThresholdOnce = true;
  };

  /**
   * Finds the quantity selector for a specific cart line number.
   * Cart line numbers are NOT sequential - they can have gaps if items were removed.
   * @param {number} line - The cart line number (from data-cart-line attribute)
   * @returns {any} The quantity selector element or null (typed as any to allow custom component methods)
   */
  #findQuantitySelectorByLine(line) {
    const { quantitySelectors } = this.refs;
    if (!quantitySelectors) return null;

    // Find the quantity selector whose input has matching data-cart-line
    return (
      quantitySelectors.find(selector => {
        const input = selector.querySelector("input");
        return input && Number(input.dataset.cartLine) === line;
      }) || null
    );
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener("theme:quantitySelectorBundleUpdate", this._onBundleQuantityChange);
    document.addEventListener("theme:bundleRemove", this._onBundleRemove);

    // Handle bundle remove buttons directly
    this.addEventListener("click", this._handleBundleRemoveClick);

    // Listen for form submissions within cart upsell to show loading immediately
    this.addEventListener("submit", this.#handleFormSubmit);

    // Listen for cart:add-start event (from product-add-to-cart.js) to show loading immediately
    document.addEventListener("cart:add-start", this.#handleCartAddStart);

    this.addEventListener("cart-discount:manual-remove", this.#onDiscountManualRemove);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener("cart-discount:manual-remove", this.#onDiscountManualRemove);

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener("theme:quantitySelectorBundleUpdate", this._onBundleQuantityChange);
    document.removeEventListener("theme:bundleRemove", this._onBundleRemove);
    this.removeEventListener("click", this._handleBundleRemoveClick);
    this.removeEventListener("submit", this.#handleFormSubmit);
    document.removeEventListener("cart:add-start", this.#handleCartAddStart);
  }

  /**
   * Handles bundle quantity update event.
   * Prefers line-based updates when bundleLines is available (unambiguous),
   * falls back to variant-ID-based updates for backward compatibility.
   * @param {CustomEvent} event - The bundle update event.
   */
  #onBundleQuantityChange(event) {
    const { bundleIds, bundleLines, quantity } = event.detail;

    if (bundleLines) {
      this.#updateBundleByLines(bundleLines, quantity);
    } else {
      // Fallback to variant-ID-based update
      this.#updateBundleByVariantIds(bundleIds, quantity);
    }
  }

  /**
   * Handles bundle removal event.
   * @param {CustomEvent} event - The bundle removal event.
   */
  #onBundleRemove(event) {
    const { lines } = event.detail;
    // console.log("Handling bundle removal:", { lines });

    // Remove all bundle items by setting quantity to 0
    this.updateBundleQuantity(lines, 0);
  }

  /**
   * Handles click events on bundle remove buttons.
   * Prefers line-based removal when data-bundle-lines is available (unambiguous),
   * falls back to variant-ID-based removal for backward compatibility.
   * @param {MouseEvent} event - The click event.
   */
  #handleBundleRemoveClick(event) {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (!target) return;

    const removeButton = target.closest("[data-bundle-remove]");
    if (!removeButton) return;

    event.preventDefault();
    event.stopPropagation();

    const bundleRow = removeButton.closest("[data-bundle-ids]");
    if (!bundleRow || !(bundleRow instanceof HTMLElement)) {
      console.error("Bundle row not found for remove button");
      return;
    }

    // Prefer key-based removal (stable IDs, no index shifting)
    const bundleKeys = bundleRow.dataset.bundleKeys;
    if (bundleKeys) {
      this.#removeBundleByKeys(bundleKeys);
      return;
    }

    // Fallback: line-based removal (requires descending order to avoid index shift)
    const bundleLines = bundleRow.dataset.bundleLines;
    if (bundleLines) {
      this.#removeBundleByLines(bundleLines);
      return;
    }

    // Last resort: variant-ID-based removal
    const bundleIds = bundleRow.dataset.bundleIds;
    if (!bundleIds) {
      console.error("Bundle IDs not found");
      return;
    }

    this.#removeBundleByVariantIds(bundleIds);
  }

  /**
   * Removes bundle items in a single cart/update.js request using stable line item keys.
   * cart/update.js accepts both variant IDs and line item keys — using keys avoids
   * cross-contamination when two bundles share the same variant.
   * @param {string} bundleKeys - Comma-separated cart line item keys (e.g. "12345:abc,67890:def")
   */
  async #removeBundleByKeys(bundleKeys) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker("bundle-keys-remove:user-action");

    this.#disableCartItems();

    const { cartTotal } = this.refs;
    cartTotal?.shimmer();

    const keys = bundleKeys
      .split(",")
      .map(k => k.trim())
      .filter(Boolean);

    /** @type {Record<string, number>} */
    const updates = {};
    keys.forEach(key => {
      updates[key] = 0;
    });

    try {
      // Single request removes all bundle items at once — no sequential calls, no UX flicker
      const response = await fetch(/** @type {any} */ (window.Shopify).routes.root + "cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const cart = await response.json();

      resetShimmer(this);

      const cartItemsComponents = document.querySelectorAll("cart-items-component");
      const sectionsToUpdate = new Set([this.sectionId]);
      cartItemsComponents.forEach(item => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      });

      const sectionsUrl = `${window.location.pathname}?sections=${Array.from(sectionsToUpdate).join(",")}`;
      const sectionsResponse = await fetch(sectionsUrl);
      const sections = await sectionsResponse.json();

      const newSectionHTML = new DOMParser().parseFromString(sections[this.sectionId], "text/html");
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      const prevLoyalty = snapshotLoyaltyCodesFromScope(this);

      this.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId, {
          itemCount: newCartItemCount,
          source: "cart-items-component",
          sections: sections,
        })
      );

      await morphSection(this.sectionId, sections[this.sectionId]);

      this.#notifyLoyaltyThresholdIfNeeded(prevLoyalty, cart);
    } catch (error) {
      console.error("Error removing bundle by keys:", error);
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Removes bundle items by variant IDs using cart/update.js
   * @param {string} bundleIds - Comma-separated variant IDs
   */
  async #removeBundleByVariantIds(bundleIds) {
    // console.log("Removing bundle by variant IDs:", bundleIds);

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker("bundle-remove:user-action");

    this.#disableCartItems();

    const { cartTotal } = this.refs;
    cartTotal?.shimmer();

    // Build updates object with all variant IDs set to 0
    /** @type {Record<string, number>} */
    const updates = {};
    const variantIds = bundleIds.split(",").map(id => id.trim());
    variantIds.forEach(variantId => {
      if (variantId) {
        updates[variantId] = 0;
      }
    });

    // console.log("Removing bundle items:", updates);

    try {
      const response = await fetch(/** @type {any} */ (window.Shopify).routes.root + "cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ updates }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const cart = await response.json();

      resetShimmer(this);

      const cartItemsComponents = document.querySelectorAll("cart-items-component");
      const sectionsToUpdate = new Set([this.sectionId]);
      cartItemsComponents.forEach(item => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      });

      const sectionsUrl = `${window.location.pathname}?sections=${Array.from(sectionsToUpdate).join(",")}`;
      const sectionsResponse = await fetch(sectionsUrl);
      const sections = await sectionsResponse.json();

      const newSectionHTML = new DOMParser().parseFromString(sections[this.sectionId], "text/html");
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      const prevLoyalty = snapshotLoyaltyCodesFromScope(this);

      this.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId, {
          itemCount: newCartItemCount,
          source: "cart-items-component",
          sections: sections,
        })
      );

      await morphSection(this.sectionId, sections[this.sectionId]);
      this.#notifyLoyaltyThresholdIfNeeded(prevLoyalty, cart);
    } catch (error) {
      console.error("Error removing bundle:", error);
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Updates multiple cart items sequentially
   * @param {number[]} lines - Array of line numbers to update
   * @param {number} quantity - The quantity to set for all lines
   */
  // updateBundleQuantity(lines, quantity) {
  //   console.log('Updating bundle lines:', lines, 'to quantity:', quantity);

  //   // Update each line sequentially with delay to avoid race conditions
  //   lines.forEach((/** @type {number} */ line, /** @type {number} */ index) => {
  //     setTimeout(() => {
  //       this.updateQuantity({
  //         line,
  //         quantity,
  //         action: 'change',
  //       });
  //     }, index * 100); // 100ms delay between updates
  //   });
  // }
  /**
   * Updates bundle items by variant IDs directly
   * @param {string} bundleIds - Comma-separated variant IDs
   * @param {number} quantity - The quantity to set for all variants
   */
  async #updateBundleByVariantIds(bundleIds, quantity) {
    // console.log("Updating bundle by variant IDs:", bundleIds, "to quantity:", quantity);

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker("bundle:user-action");

    this.#disableCartItems();

    const { cartTotal } = this.refs;
    cartTotal?.shimmer();

    // Build updates object with variant IDs
    /** @type {Record<string, number>} */
    const updates = {};
    const variantIds = bundleIds.split(",").map(id => id.trim());
    variantIds.forEach(variantId => {
      if (variantId) {
        updates[variantId] = quantity;
      }
    });

    // console.log("Updates object:", updates);

    try {
      const response = await fetch(/** @type {any} */ (window.Shopify).routes.root + "cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ updates }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const cart = await response.json();

      resetShimmer(this);

      const cartItemsComponents = document.querySelectorAll("cart-items-component");
      const sectionsToUpdate = new Set([this.sectionId]);
      cartItemsComponents.forEach(item => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      });

      const sectionsUrl = `${window.location.pathname}?sections=${Array.from(sectionsToUpdate).join(",")}`;
      const sectionsResponse = await fetch(sectionsUrl);
      const sections = await sectionsResponse.json();

      const newSectionHTML = new DOMParser().parseFromString(sections[this.sectionId], "text/html");
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      this.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId, {
          itemCount: newCartItemCount,
          source: "cart-items-component",
          sections: sections,
        })
      );

      morphSection(this.sectionId, sections[this.sectionId]);
    } catch (error) {
      console.error("Error updating bundle:", error);
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Updates bundle items by 1-based line indices using sequential cart/change.js calls.
   * Line-based operations are unambiguous — they target exact cart positions,
   * avoiding cross-contamination when the same variant appears in multiple bundles.
   * @param {string} bundleLines - Comma-separated 1-based line numbers (e.g. "2,5,7")
   * @param {number} quantity - The quantity to set for all lines
   */
  async #updateBundleByLines(bundleLines, quantity) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker("bundle-lines:user-action");

    this.#disableCartItems();

    const { cartTotal } = this.refs;
    cartTotal?.shimmer();

    const lines = bundleLines
      .split(",")
      .map(l => parseInt(l.trim(), 10))
      .filter(n => !isNaN(n));

    try {
      // Sequential cart/change.js calls — one per line item
      for (const line of lines) {
        const response = await fetch(/** @type {any} */ (window.Shopify).routes.root + "cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ line, quantity }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status} for line ${line}`);
        }

        await response.json();
      }

      resetShimmer(this);

      // Fetch updated section HTML after all lines are updated
      const cartItemsComponents = document.querySelectorAll("cart-items-component");
      const sectionsToUpdate = new Set([this.sectionId]);
      cartItemsComponents.forEach(item => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      });

      const sectionsUrl = `${window.location.pathname}?sections=${Array.from(sectionsToUpdate).join(",")}`;
      const sectionsResponse = await fetch(sectionsUrl);
      const sections = await sectionsResponse.json();

      const newSectionHTML = new DOMParser().parseFromString(sections[this.sectionId], "text/html");
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      this.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId, {
          itemCount: newCartItemCount,
          source: "cart-items-component",
          sections: sections,
        })
      );

      morphSection(this.sectionId, sections[this.sectionId]);
    } catch (error) {
      console.error("Error updating bundle by lines:", error);
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Removes bundle items by 1-based line indices using sequential cart/change.js calls.
   * Lines are processed in descending order (highest first) to avoid index shifting
   * as items are removed from the cart.
   * @param {string} bundleLines - Comma-separated 1-based line numbers (e.g. "2,5,7")
   */
  async #removeBundleByLines(bundleLines) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker("bundle-lines-remove:user-action");

    this.#disableCartItems();

    const { cartTotal } = this.refs;
    cartTotal?.shimmer();

    // Sort descending so removing higher indices first doesn't shift lower indices
    const lines = bundleLines
      .split(",")
      .map(l => parseInt(l.trim(), 10))
      .filter(n => !isNaN(n))
      .sort((a, b) => b - a);

    try {
      // Sequential cart/change.js calls — highest line first to avoid index shifting
      for (const line of lines) {
        const response = await fetch(/** @type {any} */ (window.Shopify).routes.root + "cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ line, quantity: 0 }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status} for line ${line}`);
        }

        await response.json();
      }

      resetShimmer(this);

      // Fetch updated section HTML after all lines are removed
      const cartItemsComponents = document.querySelectorAll("cart-items-component");
      const sectionsToUpdate = new Set([this.sectionId]);
      cartItemsComponents.forEach(item => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      });

      const sectionsUrl = `${window.location.pathname}?sections=${Array.from(sectionsToUpdate).join(",")}`;
      const sectionsResponse = await fetch(sectionsUrl);
      const sections = await sectionsResponse.json();

      const newSectionHTML = new DOMParser().parseFromString(sections[this.sectionId], "text/html");
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      const prevLoyalty = snapshotLoyaltyCodesFromScope(this);

      this.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId, {
          itemCount: newCartItemCount,
          source: "cart-items-component",
          sections: sections,
        })
      );

      await morphSection(this.sectionId, sections[this.sectionId]);

      // Fetch cart JSON for loyalty threshold check
      const cartResponse = await fetch(/** @type {any} */ (window.Shopify).routes.root + "cart.js");
      const cart = await cartResponse.json();
      this.#notifyLoyaltyThresholdIfNeeded(prevLoyalty, cart);
    } catch (error) {
      console.error("Error removing bundle by lines:", error);
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Updates multiple cart items in a single request
   * @param {number[]} lines - Array of line numbers to update
   * @param {number} quantity - The quantity to set for all lines
   * @deprecated Use #updateBundleByLines instead
   */
  async updateBundleQuantity(lines, quantity) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker("bundle:user-action");

    this.#disableCartItems();

    const { cartTotal } = this.refs;
    cartTotal?.shimmer();

    /** @type {Record<string, number>} */
    const updates = {};

    lines.forEach(line => {
      const lineItemRow = this.refs.cartItemRows[line - 1];
      if (lineItemRow) {
        const bundleIds = lineItemRow.dataset.bundleIds;

        if (bundleIds) {
          const variantIds = bundleIds.split(",").map(id => id.trim());
          variantIds.forEach(variantId => {
            if (variantId) {
              updates[variantId] = quantity;
            }
          });
        }
      }
    });

    try {
      const response = await fetch(/** @type {any} */ (window.Shopify).routes.root + "cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ updates }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const cart = await response.json();

      resetShimmer(this);

      const cartItemsComponents = document.querySelectorAll("cart-items-component");
      const sectionsToUpdate = new Set([this.sectionId]);
      cartItemsComponents.forEach(item => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      });

      const sectionsUrl = `${window.location.pathname}?sections=${Array.from(sectionsToUpdate).join(",")}`;
      const sectionsResponse = await fetch(sectionsUrl);
      const sections = await sectionsResponse.json();

      const newSectionHTML = new DOMParser().parseFromString(sections[this.sectionId], "text/html");
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      this.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId, {
          itemCount: newCartItemCount,
          source: "cart-items-component",
          sections: sections,
        })
      );

      morphSection(this.sectionId, sections[this.sectionId]);
    } catch (error) {
      console.error("Error updating bundle:", error);
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
    }
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: "change",
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector("text-component"));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: "clear",
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter(row => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach(row => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty("--row-height", `${row.clientHeight}px`);
      row.classList.add("removing");

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;
    cartTotal?.shimmer();

    this.#doQuantityUpdate(line, quantity)
      .catch(error => console.error(error))
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * After cart morph: loyalty codes dropped by Shopify while subtotal is below minimum.
   * @param {string[]} prevLoyaltyCodes
   * @param {unknown} cartJson
   */
  #notifyLoyaltyThresholdIfNeeded(prevLoyaltyCodes, cartJson) {
    if (!cartJson || typeof cartJson !== "object") return;
    if (this.#skipLoyaltyThresholdOnce) {
      this.#skipLoyaltyThresholdOnce = false;
      return;
    }
    const msg = loyaltyThresholdLossMessage(prevLoyaltyCodes, cartJson);
    if (!msg) return;
    const host = this.querySelector("cart-discount-component");
    if (host && typeof host.showLoyaltyThresholdError === "function") {
      host.showLoyaltyThresholdError(msg);
    }
  }

  /**
   * Dispatch CartUpdateEvent and morph the section from a sections map.
   * Shared by the GWP-aware path and the regular /cart/change.js path.
   * @param {Record<string, string>} sections
   * @param {unknown} [cartJson] - Cart payload from the same response (for loyalty threshold messaging).
   */
  async #dispatchAndMorph(sections, cartJson) {
    const sectionHTML = sections?.[this.sectionId];
    if (!sectionHTML) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }

    const doc = new DOMParser().parseFromString(sectionHTML, "text/html");
    const countEl = doc.querySelector('[ref="cartItemCount"]');
    const itemCount = countEl ? parseInt(countEl.textContent || "0", 10) : 0;

    const prevLoyalty = snapshotLoyaltyCodesFromScope(this);

    this.dispatchEvent(
      new CartUpdateEvent({}, this.sectionId, {
        itemCount,
        source: "cart-items-component",
        sections,
      })
    );

    await morphSection(this.sectionId, sectionHTML);
    this.#notifyLoyaltyThresholdIfNeeded(prevLoyalty, cartJson);
  }

  /**
   * Core async implementation for quantity updates.
   * Tries the GWP-aware batch path first (/cart/update.js for removals),
   * then falls back to the regular /cart/change.js path.
   * @param {number} line
   * @param {number} quantity
   */
  async #doQuantityUpdate(line, quantity) {
    // ── GWP-aware batch path ──
    try {
      const utils = /** @type {any} */ (window).GwpCartUtils;
      if (utils?.changeQtyWithGwp) {
        const result = await utils.changeQtyWithGwp(line, quantity);
        if (result !== null) {
          resetShimmer(this);
          if (result.data?.errors) {
            this.#handleCartError(line, result.data);
            return;
          }
          utils.dispatchCartUpdate(result.data, result.sections);
          await this.#dispatchAndMorph(result.sections, result.data);
          return;
        }
      }
    } catch (e) {
      console.error(e);
    }

    // ── Regular /cart/change.js path ──
    const cartItemsComponents = document.querySelectorAll("cart-items-component");
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach(item => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line,
      quantity,
      sections: Array.from(sectionsToUpdate).join(","),
      sections_url: window.location.pathname,
    });

    const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig("json", { body }));
    const responseText = await response.text();
    const parsedResponseText = JSON.parse(responseText);

    resetShimmer(this);

    if (parsedResponseText.errors) {
      this.#handleCartError(line, parsedResponseText);
      return;
    }

    await this.#dispatchAndMorph(parsedResponseText.sections, parsedResponseText);
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = event => {
    this.#handleCartUpdate(event);
  };

  /**
   * Discount pills sit as siblings of `cart-discount-component` under this root; delegation targets that host.
   * @param {MouseEvent | KeyboardEvent} event
   */
  removeDiscount = async event => {
    const discountHost = this.querySelector("cart-discount-component");
    if (!discountHost || typeof discountHost.removeDiscount !== "function") return;
    await discountHost.removeDiscount(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    // Use helper to find by data-cart-line (line numbers can have gaps)
    const quantitySelector = this.#findQuantitySelectorByLine(line);
    const quantityInput = quantitySelector?.querySelector("input");

    if (!quantityInput) throw new Error("Quantity input not found");

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error("Cart item error not found");
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error("Cart item error container not found");

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove("hidden");
  };

  /**
   * Handles form submit events - shows loading state immediately when add-to-cart form is submitted.
   * Shows skeleton when cart is empty, spinner when cart has items.
   * @param {Event} event - The submit event.
   */
  #handleFormSubmit = event => {
    const form = /** @type {HTMLFormElement} */ (event.target);
    // Only show loading for add-to-cart forms (from cart upsell/YMAL/Complete Your Uniform)
    if (form?.getAttribute("data-type") === "add-to-cart-form") {
      const isCartEmpty = this.#isCartEmpty();
      // Show skeleton when cart is empty, spinner when cart has items
      this.#disableCartItems(isCartEmpty);
    }
  };

  /**
   * Handles cart:add-start event - shows loading state immediately when add-to-cart starts.
   * Shows skeleton loader ONLY when cart is empty (first item being added).
   * Shows spinner for subsequent PDP adds when cart already has items.
   */
  #handleCartAddStart = () => {
    const isCartEmpty = this.#isCartEmpty();
    // Only show skeleton when cart is empty (first item being added)
    // For subsequent adds, show spinner instead
    this.#disableCartItems(isCartEmpty);
  };

  /**
   * Checks if the cart is currently empty (no items)
   * @returns {boolean}
   */
  #isCartEmpty() {
    const { cartItemRows } = this.refs;
    return !cartItemRows || cartItemRows.length === 0;
  }

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = async event => {
    if (event instanceof DiscountUpdateEvent) {
      if (event.detail.domSynced) {
        this.#enableCartItems();
        return;
      }
      const discountHtml = event.detail.resource?.sections?.[this.sectionId];
      if (typeof discountHtml === "string") {
        void morphSection(this.sectionId, discountHtml).then(() => {
          this.#enableCartItems();
        });
        return;
      }
      sectionRenderer.renderSection(this.sectionId, { cache: false }).then(() => {
        this.#enableCartItems();
      });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      // Hide loading state after morph completes
      this.#enableCartItems();
    } else {
      await sectionRenderer.renderSection(this.sectionId, { cache: false });
      this.#enableCartItems();
    }

    // Reload Corso script for cart add events (bundles, YMAL, etc.)
    if (event instanceof CartAddEvent) {
      this.#reloadCorsoScript();
    }
  };

  /**
   * Ensures Corso shipping protection script is initialized.
   * Only reloads if Corso widget is not already present on the page.
   * Once injected, Corso auto-updates its price - no need to reload.
   */
  #reloadCorsoScript() {
    // If Corso widget already exists, it auto-updates - no reload needed
    if (document.querySelector(".corso-wrapper")) return;

    const corsoScript = document.querySelector('script[src*="corso.com"]');
    if (!corsoScript) return;

    const scriptSrc = corsoScript.getAttribute("src");
    if (!scriptSrc) return;

    // Remove the old script and re-inject to initialize Corso
    corsoScript.remove();

    const newScript = document.createElement("script");
    newScript.src = scriptSrc;
    newScript.async = true;
    document.head.appendChild(newScript);
  }

  /**
   * Disables the cart items and shows loading state.
   * @param {boolean} [showSkeleton=false] - Whether to show skeleton loader (for add operations)
   */
  #disableCartItems(showSkeleton = false) {
    this.classList.add("cart-items-disabled", "is_loading");
    if (showSkeleton) {
      this.classList.add("skeleton-loading");
    }
  }

  /**
   * Enables the cart items and hides loading state.
   */
  #enableCartItems() {
    this.classList.remove("cart-items-disabled", "is_loading", "skeleton-loading");
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error("Section id missing");

    return sectionId;
  }
}

if (!customElements.get("cart-items-component")) {
  customElements.define("cart-items-component", CartItemsComponent);
}
